import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { BUILDINGS } from '../../lib/workerData';
import { formatTime, formatDate } from '../../lib/utils';
import {
  isGpsMismatch, isGpsUnknown, buildExpectedMap, ROSTER_GROUP_BUILDING,
  OPERATION_START_DATE, localDateStr,
} from './TonightView';
import { buildCommandCenterData, computeStartTimeByWorker, SEVERITY } from '../../lib/commandCenter';
import CommandMap from './CommandMap';
import WorkerDetail from './WorkerDetail';

// Anchored to yesterday's local midnight rather than a rolling N hours back
// from "now" — a fixed-hours window quietly empties out a finished overnight
// shift partway through the next afternoon as the window slides forward,
// even though nothing about that shift changed. Anchoring to a calendar
// boundary means last night's activity stays visible all day today, and
// only drops off when the day actually turns over.
function sinceYesterdayMidnight() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

const SEVERITY_META = {
  [SEVERITY.CRITICAL]: { label: 'CRITICAL', color: '#B33A2E', bg: 'rgba(179,58,46,0.08)', border: '#B33A2E' },
  [SEVERITY.WARNING]: { label: 'WARNING', color: '#96600A', bg: 'rgba(181,115,10,0.08)', border: '#B5730A' },
  [SEVERITY.INFO]: { label: 'INFO', color: '#5B6B72', bg: 'rgba(91,107,114,0.06)', border: '#E4E0D8' },
};

export default function CommandCentral() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ markers: [], onSite: [], finished: [], exceptions: [], activeBuildings: [] });
  const [expectedMap, setExpectedMap] = useState(new Map());
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [resolving, setResolving] = useState(null);

  const todayLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  const load = useCallback(async () => {
    setError(null);
    const since = sinceYesterdayMidnight();
    const today = localDateStr();
    const todayDow = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const weekStart = (() => {
      const d = new Date();
      const day = d.getDay();
      d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
      return localDateStr(d);
    })();

    // Same table/column selections TonightView already uses for these same
    // five tables — kept identical on purpose so this can never quietly
    // drift from what Today considers "expected" or "checked in".
    const [subsRes, workersRes, eventsRes, rentalsRes, rosterRes, issuesRes, buildingTimesRes] = await Promise.all([
      supabase
        .from('checklist_submissions')
        .select('id, worker_id, worker_name, building_id, sign_in, sign_out, gps_lat, gps_lng, gps_accuracy, gps_distance_metres, gps_verified, gps_error')
        .gte('sign_in', since)
        .order('sign_in', { ascending: false }),
      supabase
        .from('profiles')
        .select('id, full_name, role, shift, assigned_buildings')
        .in('role', ['worker', 'supervisor']),
      supabase
        .from('scheduled_events')
        .select('id, building_id, location, assigned_workers, assigned_worker_names, start_date, end_date')
        .lte('start_date', today)
        .gte('end_date', today),
      supabase
        .from('rental_schedules')
        .select('id, building_id, week_start, assigned_workers, assigned_worker_names, windows')
        .eq('week_start', weekStart),
      supabase
        .from('shift_assignments')
        .select('building_group, day_of_week, shift_type, worker_id, worker_name')
        .in('building_group', Object.keys(ROSTER_GROUP_BUILDING))
        .eq('day_of_week', todayDow),
      // Not bounded to the lookback window — an unresolved issue keeps
      // showing up until someone deals with it, same as Today's issues list.
      supabase
        .from('checklist_submissions')
        .select('id, worker_id, worker_name, building_id, sign_in, maintenance_note')
        .eq('maintenance_flag', true)
        .eq('maintenance_resolved', false)
        .order('sign_in', { ascending: false }),
      // Real day/night start times per building — Today has no equivalent
      // query since it doesn't gate no-shows on time of day at all; this is
      // new, purely for Command Central's start-time gating below.
      supabase
        .from('buildings')
        .select('building_id, day_start, day_end, night_start, night_end'),
    ]);

    const firstErr = subsRes.error || workersRes.error || eventsRes.error || rentalsRes.error || rosterRes.error;
    if (firstErr) {
      setError(firstErr.message);
      setLoading(false);
      return;
    }

    const expected = buildExpectedMap(
      workersRes.data || [], eventsRes.data, rentalsRes.data, rosterRes.data || [],
      today >= OPERATION_START_DATE, todayDow,
    );

    const buildingTimes = new Map(
      (buildingTimesRes.error ? [] : (buildingTimesRes.data || []))
        .map(b => [b.building_id, b]),
    );
    const startTimeByWorker = computeStartTimeByWorker({
      roster: rosterRes.data || [], rentals: rentalsRes.data || [], buildingTimes, todayDow,
    });

    const built = buildCommandCenterData({
      subs: subsRes.data || [],
      expected,
      buildings: BUILDINGS,
      issues: issuesRes.error ? [] : (issuesRes.data || []),
      isGpsMismatch, isGpsUnknown, startTimeByWorker, today,
    });

    setData(built);
    setExpectedMap(expected);
    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Same existing, already-reviewed write path Today's "Mark resolved"
  // button uses — no new write operation introduced here.
  async function resolveIssue(e, subId) {
    e.stopPropagation();
    setResolving(subId);
    const { error: resolveErr } = await supabase
      .from('checklist_submissions')
      .update({ maintenance_resolved: true })
      .eq('id', subId);
    setResolving(null);
    if (resolveErr) {
      console.error('Could not mark issue resolved:', resolveErr);
      return;
    }
    load();
  }

  const { markers, onSite, finished, exceptions, activeBuildings } = data;
  const critCount = exceptions.filter(e => e.severity === SEVERITY.CRITICAL).length;
  const warnCount = exceptions.filter(e => e.severity === SEVERITY.WARNING).length;
  const selectedWorkerBuildings = selectedWorker && expectedMap.get(selectedWorker)
    ? [...expectedMap.get(selectedWorker).buildings]
    : [];

  return (
    <div style={styles.page}>
      {/* Below 860px the status board's 280px minimum leaves almost nothing
          for the map — it doesn't disappear, it just squeezes down to an
          unusable sliver. Below that width the map and status board stack
          instead of sitting side by side. */}
      <style>{`
        @media (max-width: 860px) {
          .cc-grid { grid-template-columns: 1fr !important; }
          .cc-map-pane { min-height: 320px !important; }
        }
      `}</style>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Command Central</div>
          <div style={styles.sub}>
            {todayLabel} · {onSite.length} on-site · {exceptions.length} exception{exceptions.length === 1 ? '' : 's'}
            {lastRefresh ? ` · updated ${formatTime(lastRefresh)}` : ''}
          </div>
        </div>
        <div style={styles.headerCounts}>
          <CountPill color="#B33A2E" count={critCount} label="critical" />
          <CountPill color="#96600A" count={warnCount} label="warning" />
          <button style={styles.refreshBtn} onClick={() => { setLoading(true); load(); }} disabled={loading}>
            {loading ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={styles.errorBox}>
          Could not load Command Central. <button style={styles.retryBtn} onClick={load}>Retry</button>
        </div>
      )}

      {loading && !lastRefresh ? (
        <div style={styles.loadingState}>Loading live status…</div>
      ) : (
        <div className="cc-grid" style={styles.grid}>
          <div className="cc-map-pane" style={styles.mapPane}>
            <CommandMap markers={markers} buildings={activeBuildings} onSelectWorker={setSelectedWorker} />
          </div>

          <div style={styles.statusPane}>
            <div style={styles.paneHead}>
              <span style={{ ...styles.paneDot, background: '#2F7A4F' }} />
              <span style={styles.paneTitle}>STATUS BOARD</span>
            </div>
            <div style={styles.paneScroll}>
              <BoardSection title="ON SITE" color="#2F7A4F" count={onSite.length}>
                {onSite.map(row => (
                  <StatusRow key={row.subId || row.workerId} row={row} tone="green" onClick={() => setSelectedWorker(row.workerId)} />
                ))}
              </BoardSection>

              <ExceptionsSection
                exceptions={exceptions}
                resolving={resolving}
                onSelectWorker={setSelectedWorker}
                onResolve={resolveIssue}
              />

              <BoardSection title="FINISHED" color="#5B6B72" count={finished.length}>
                {finished.map(row => (
                  <StatusRow key={row.subId || row.workerId} row={row} tone="grey" onClick={() => setSelectedWorker(row.workerId)} />
                ))}
              </BoardSection>
            </div>
          </div>
        </div>
      )}

      {selectedWorker && (
        <WorkerDetail
          workerId={selectedWorker}
          expectedBuildings={selectedWorkerBuildings}
          onClose={() => setSelectedWorker(null)}
        />
      )}
    </div>
  );
}

function CountPill({ color, count, label }) {
  return (
    <div style={{ ...styles.countPill, borderColor: color, color }}>
      <span style={{ fontWeight: 800, fontSize: 14 }}>{count}</span> {label}
    </div>
  );
}

function ExceptionsSection({ exceptions, resolving, onSelectWorker, onResolve }) {
  return (
    <div style={styles.boardSection}>
      <div style={styles.boardSectionHead}>
        <span style={{ ...styles.paneDot, background: '#B33A2E' }} />
        <span style={{ ...styles.boardSectionTitle, color: '#B33A2E' }}>EXCEPTIONS</span>
        <span style={styles.paneCount}>{exceptions.length}</span>
      </div>
      {exceptions.length === 0 ? (
        <div style={styles.emptyPane}>Nothing needs attention right now.</div>
      ) : (
        <div style={styles.boardList}>
          {exceptions.map(exc => {
            const meta = SEVERITY_META[exc.severity];
            return (
              <div key={exc.id} role="button" tabIndex={0} style={{ ...styles.excCard, background: meta.bg, borderColor: meta.border }}
                onClick={() => exc.workerId && onSelectWorker(exc.workerId)}
                onKeyDown={(e) => { if (e.key === 'Enter' && exc.workerId) onSelectWorker(exc.workerId); }}>
                <div style={styles.excTop}>
                  <span style={{ ...styles.excSeverity, color: meta.color }}>{meta.label}</span>
                </div>
                <div style={styles.excTitle}>{exc.title}</div>
                <div style={styles.excDetail}>{exc.detail}</div>
                {exc.type === 'maintenance' && (
                  <button style={styles.resolveBtn} disabled={resolving === exc.subId}
                    onClick={(e) => onResolve(e, exc.subId)}>
                    {resolving === exc.subId ? 'Marking resolved…' : '✓ Mark resolved'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BoardSection({ title, color, count, children }) {
  return (
    <div style={styles.boardSection}>
      <div style={styles.boardSectionHead}>
        <span style={{ ...styles.paneDot, background: color }} />
        <span style={{ ...styles.boardSectionTitle, color }}>{title}</span>
        <span style={styles.paneCount}>{count}</span>
      </div>
      {count === 0 ? <div style={styles.emptyPane}>None right now.</div> : <div style={styles.boardList}>{children}</div>}
    </div>
  );
}

// Night shifts routinely start before midnight and finish after — without
// this, a card timestamped "5:29p-1:21a" reads as if it happened today.
function shiftDayLabel(signIn) {
  if (!signIn) return '';
  const day = localDateStr(new Date(signIn));
  const today = localDateStr();
  if (day === today) return '';
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (day === localDateStr(yesterday)) return 'Yesterday · ';
  return `${formatDate(signIn)} · `;
}

function StatusRow({ row, tone, onClick }) {
  const border = tone === 'green' ? '#2F7A4F' : '#E4E0D8';
  const bg = tone === 'green' ? 'rgba(47,122,79,0.07)' : '#FFFFFF';
  return (
    <button style={{ ...styles.statusRow, borderColor: border, background: bg }} onClick={onClick}>
      <div style={styles.statusRowTop}>
        <span style={styles.statusName}>{row.name}</span>
        {row.gpsMismatch && <span style={styles.miniFlag}>⚠ off-site</span>}
        {row.gpsUnknown && <span style={styles.miniFlagGrey}>? no GPS</span>}
      </div>
      <div style={styles.statusDetail}>
        {shiftDayLabel(row.signIn)}{row.buildingName} · {row.signOut ? `${formatTime(row.signIn)}–${formatTime(row.signOut)}` : `in ${formatTime(row.signIn)}`}
      </div>
    </button>
  );
}

const styles = {
  page: { padding: '18px 18px 24px', fontFamily: "'Segoe UI', system-ui, sans-serif", display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 130px)', background: '#F5F3EF' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  title: { color: '#1A2B33', fontSize: 24, fontWeight: 800, letterSpacing: -0.5 },
  sub: { color: '#5B6B72', fontSize: 12.5, marginTop: 4 },
  headerCounts: { display: 'flex', alignItems: 'center', gap: 10 },
  countPill: { border: '1px solid', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 },
  refreshBtn: { background: '#1F3A47', border: '1px solid #1F3A47', color: '#FFFFFF', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  errorBox: { background: '#FBF1EF', border: '1px solid #B33A2E', borderRadius: 10, padding: 14, color: '#B33A2E', fontSize: 13, marginBottom: 16 },
  retryBtn: { background: '#1F3A47', border: 'none', color: '#FFFFFF', borderRadius: 6, padding: '5px 10px', fontWeight: 700, fontSize: 12, cursor: 'pointer', marginLeft: 8 },
  loadingState: { padding: '60px 0', textAlign: 'center', color: '#5B6B72', fontSize: 14 },
  grid: {
    display: 'grid', gridTemplateColumns: 'minmax(0, 2.7fr) minmax(280px, 1fr)',
    gap: 14, flex: 1, minHeight: 520,
  },
  mapPane: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, minHeight: 420, overflow: 'hidden' },
  statusPane: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, display: 'flex', flexDirection: 'column', minHeight: 0 },
  paneHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px 10px' },
  paneDot: { width: 8, height: 8, borderRadius: 999, flexShrink: 0 },
  paneTitle: { fontSize: 12, fontWeight: 800, letterSpacing: 1, color: '#1A2B33' },
  paneCount: { marginLeft: 'auto', color: '#5B6B72', fontSize: 12, fontWeight: 700 },
  paneScroll: { flex: 1, overflowY: 'auto', padding: '0 12px 12px' },
  emptyPane: { color: '#8B98A0', fontSize: 12.5, padding: '10px 6px' },
  excCard: { display: 'block', width: '100%', textAlign: 'left', border: '1px solid', borderRadius: 10, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', font: 'inherit' },
  excTop: { display: 'flex', marginBottom: 4 },
  excSeverity: { fontSize: 10, fontWeight: 800, letterSpacing: 0.8 },
  excTitle: { color: '#1A2B33', fontWeight: 700, fontSize: 13.5, marginBottom: 3 },
  excDetail: { color: '#5B6B72', fontSize: 12, lineHeight: 1.4 },
  resolveBtn: { marginTop: 8, background: '#FFFFFF', border: '1px solid #B5730A', color: '#96600A', borderRadius: 8, padding: '5px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' },
  boardSection: { marginBottom: 16 },
  boardSectionHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '0 4px' },
  boardSectionTitle: { fontSize: 11, fontWeight: 800, letterSpacing: 0.8 },
  boardList: { display: 'flex', flexDirection: 'column', gap: 6 },
  statusRow: { display: 'block', width: '100%', textAlign: 'left', border: '1px solid', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', font: 'inherit' },
  statusRowTop: { display: 'flex', alignItems: 'center', gap: 8 },
  statusName: { color: '#1A2B33', fontWeight: 700, fontSize: 13 },
  statusDetail: { color: '#5B6B72', fontSize: 11.5, marginTop: 2 },
  miniFlag: { fontSize: 10.5, color: '#96600A', fontWeight: 700 },
  miniFlagGrey: { fontSize: 10.5, color: '#8B98A0', fontWeight: 700 },
};
